<script lang="ts">
	import type { MapGeoJSONFeature } from 'maplibre-gl';
	import type { TInterventionsSignage } from '../columns';
	import { attributesColumns as safecurvesAttrsCols } from '../layerConfig';
	import { attributesColumns as crashesAttrsCols } from '#lib/apps/safesystem/context/columns.js';
	import styleSignsHalo from '../layer_styles/safecurves_interventions_curve-advisory-signs-halo_style.json';
	import { getAccuracyWarnings } from '../alertsAndWarnings';
	import SpriteIcon from '#lib/components/map/legend/SpriteIcon.svelte';
	import { safeCurvesSpriteSheetPath, safeCurvesOutlineSheetPath } from '../loadMapSprites';
	import {
		parseGeometryLocation,
		formatPointGeometry,
		parseLinestringEndpoints
	} from '#lib/featureGeometry.js';
	import { EIdentify, EInterventions, EPrioritize } from '../interfaces';
	import { ECrashes } from '#lib/apps/safesystem/context/interfaces.js';
	import ClearZoneCrossSection from './ClearZoneCrossSection.svelte';
	import FeatureAttributesPanel, {
		layerPatchColor
	} from '#lib/components/FeatureAttributesPanel.svelte';
	import LegendPatch from '#lib/components/LegendPatch.svelte';
	import LegendIcon from '#lib/components/LegendIcon.svelte';
	import AbleyButton from '#lib/components/AbleyButton.svelte';
	import { programsPath } from '../programs/breadcrumbs.svelte';

	// Programs layers, whose features link back to their project.
	const programsLayers: string[] = [
		EPrioritize.ProgramProjects,
		EInterventions.ExistingSigns,
		EInterventions.ExistingChevronAreas,
		EInterventions.ExistingSpeedPlaques,
		EInterventions.ExistingBarriers
	];
	const squarePatch: string[] = [
		EPrioritize.Prioritize,
		EPrioritize.ProgramProjects,
		EInterventions.ExistingBarriers
	];

	const columns = { ...crashesAttrsCols, ...safecurvesAttrsCols };

	// Evaluate a MapLibre ["match", input, k1, v1, …, default] against a value;
	// a label slot may itself be an array of values sharing one output.
	function matchValue(expr: unknown, value: string | undefined): string | undefined {
		if (!Array.isArray(expr) || expr[0] !== 'match') return undefined;
		for (let i = 2; i < expr.length - 1; i += 2) {
			const label = expr[i];
			if (Array.isArray(label) ? label.includes(value) : label === value) return expr[i + 1];
		}
		return expr[expr.length - 1];
	}

	// Sprite ids mirror the `icon-image` expressions in the sign/speed layer styles.
	function signsIcon(feature: MapGeoJSONFeature): string {
		const a = feature.properties as TInterventionsSignage;
		const code = a.advisory_sign?.match(/\(([^)]+)\)/)?.[1];
		const dir = a.advisory_sign_direction;
		if (!code || !dir) return 'fallback';
		return `advisory_sign_${code}${dir === 'Both' ? 'L' : dir.slice(0, 1)}`;
	}

	// Tinted outline behind the sign, derived from the halo layer style so it
	// stays in step with the map halo (shape by advisory_sign, colour by recommendation).
	function signsHalo(feature: MapGeoJSONFeature, icon: string) {
		if (icon === 'fallback') return undefined;
		const a = feature.properties as TInterventionsSignage;
		const haloIcon = matchValue(styleSignsHalo.layout['icon-image'], a.advisory_sign);
		const color = matchValue(styleSignsHalo.paint['icon-color'], a.advisory_sign_recommendation);
		return haloIcon && color
			? { icon: haloIcon, color, spritePath: safeCurvesOutlineSheetPath() }
			: undefined;
	}

	function enrich(
		attrs: Record<string, string | number>,
		feature: MapGeoJSONFeature,
		systemId: string
	) {
		if (
			systemId === EInterventions.CurveAdvisorySigns ||
			systemId === EInterventions.CurveAdvisorySpeeds
		) {
			const position = parseGeometryLocation(feature.geometry);
			if (position) attrs.position = formatPointGeometry(position);
		}

		if (
			systemId === EInterventions.PavementFrictionFocusAreas ||
			systemId === EInterventions.StoppingSightDistance ||
			systemId === EInterventions.PassingSightDistance
		) {
			const endpoints = parseLinestringEndpoints(feature.geometry);
			if (endpoints?.[0]) attrs.startpoint = formatPointGeometry(endpoints[0]);
			if (endpoints?.[1]) attrs.endpoint = formatPointGeometry(endpoints[1]);
		}
	}
</script>

<FeatureAttributesPanel
	{columns}
	{enrich}
	alerts={(systemId, attrs) =>
		systemId === EPrioritize.Prioritize || systemId === EIdentify.Identify
			? getAccuracyWarnings(systemId, attrs as Parameters<typeof getAccuracyWarnings>[1])
			: []}>
	{#snippet icon(feature, systemId)}
		{#if squarePatch.includes(systemId) || systemId === EIdentify.Identify || systemId === ECrashes.CrashesCircle}
			<LegendPatch
				color={layerPatchColor(feature.layer)}
				shape={squarePatch.includes(systemId) ? 'square' : 'round'} />
		{:else if systemId === EInterventions.CurveAdvisorySigns}
			{@const icon = signsIcon(feature)}
			<LegendIcon size="md">
				<SpriteIcon
					{icon}
					spritePath={safeCurvesSpriteSheetPath()}
					size={30}
					fallback="fallback"
					halo={signsHalo(feature, icon)} />
			</LegendIcon>
		{:else if systemId === EInterventions.ExistingSigns || systemId === EInterventions.ExistingChevronAreas}
			<LegendIcon size="md">
				<SpriteIcon
					icon={signsIcon(feature)}
					spritePath={safeCurvesSpriteSheetPath()}
					size={30}
					fallback="fallback" />
			</LegendIcon>
		{:else if systemId === EInterventions.CurveAdvisorySpeeds || systemId === EInterventions.ExistingSpeedPlaques}
			<LegendIcon size="sm">
				<SpriteIcon
					icon={`speed_plaque_${(feature.properties as TInterventionsSignage).advisory_plaque_value}`}
					spritePath={safeCurvesSpriteSheetPath()}
					size={24} />
			</LegendIcon>
		{/if}
	{/snippet}

	{#snippet extra(feature, systemId)}
		{#if systemId === EInterventions.ClearZone}
			{@const p = feature.properties as Record<string, string | number>}
			<ClearZoneCrossSection
				clearzone_grade={p.clearzone_grade as string}
				cross_section={p.cross_section as string}
				clearzone_start_offset_m={p.clearzone_start_offset_m as number}
				backslope_distance_m={p.backslope_distance_m as number}
				foreslope_distance_m={p.foreslope_distance_m as number} />
		{:else if programsLayers.includes(systemId)}
			{@const p = feature.properties as Record<string, string>}
			<AbleyButton variant="link" href={programsPath(p.program_id, p.project_id)}>
				Open project
			</AbleyButton>
		{/if}
	{/snippet}
</FeatureAttributesPanel>
